import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Stack, Typography } from '@mui/material';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import PaiButton from 'features/ui/base-components/pai-button';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactFlow } from 'reactflow';
import { makeStyles } from 'tss-react/mui';
import { CampaignTestingState } from '../../campaign-builder.types';
import MessagePreview from './message-preview';
import SelectContactsTable from './select-contacts-table';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import { ProductGroups } from 'enums';

interface Props {
  campaignTesting: CampaignTestingState;
  finalContacts: FunnelContact[];
  finalContactsProcessing: boolean;
  autoSyncLoading: boolean;

  productGroup: ProductGroups;

  handleSelectedTestContactChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleNextContact(): void;

  handlePrevContact(): void;

  handleNextMessage(): void;

  handlePrevMessage(): void;

  handleTestCampaign(): void;
}

const CampaignTesting: React.FC<Props> = ({
  campaignTesting,
  finalContacts,
  finalContactsProcessing,
  autoSyncLoading,
  productGroup,
  handleSelectedTestContactChange,
  handleNextContact,
  handlePrevContact,
  handleNextMessage,
  handlePrevMessage,
  handleTestCampaign,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { getNodes } = useReactFlow();
  const nodes = getNodes();

  const contactsSlice = useMemo(() => {
    const filteredContacts = finalContacts.filter((contact) => contact.preprocessedProfileAnalysis);
    return _.take(filteredContacts, 5);
  }, [finalContacts]);

  const notEnoughContactsSelected = campaignTesting.selectedTestContacts.length < 1;
  const noNodesInFlow = _.isEmpty(nodes);
  const notEveryNodeHasFunctionName = !_.every(nodes, (node) => node.data.functionName);

  const testCampaignDisabled = notEnoughContactsSelected || noNodesInFlow;

  const lastTest = campaignTesting.lastTest;
  const lastTestTime = lastTest
    ? new Date(lastTest.finishedAt || lastTest.startedAt).toLocaleString(undefined, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '';
  const lastTestText = !lastTest
    ? ''
    : lastTest.status === 'running'
      ? t('testRunningFromSource', { source: t(lastTest.source === 'mcp' ? 'testSourceMcp' : 'testSourceApp') })
      : lastTest.status === 'done'
        ? t('testShownFromSource', { source: t(lastTest.source === 'mcp' ? 'testSourceMcp' : 'testSourceApp'), time: lastTestTime })
        : t('testLastFailed', { time: lastTestTime });

  return (
    <div>
      <section className={classes.section}>
        <ArrowRightAltIcon className={cx(classes.arrow, 'active')} />
        <Typography className={classes.subTitle} mb={'8px'}>
          {t('selectMaxThreeCandidates')}
        </Typography>
        <Typography className={classes.greyText}>{getLocalizedText('selectContactsForTest', productGroup, t)}</Typography>
      </section>
      <Stack gap={3}>
        <SelectContactsTable
          contacts={contactsSlice}
          selectedTestContacts={campaignTesting.selectedTestContacts}
          finalContactsProcessing={finalContactsProcessing}
          handleSelectedTestContactChange={handleSelectedTestContactChange}
        />
        <Stack direction={'row'} justifyContent={'center'}>
          <PaiButton
            text={t('testCampaign')}
            border={'rounded'}
            disabled={testCampaignDisabled || campaignTesting.loading || autoSyncLoading}
            action={handleTestCampaign}
          />
        </Stack>
        {lastTestText ? (
          <Typography className={classes.greyText} align={'center'}>
            {lastTestText}
          </Typography>
        ) : null}
        <MessagePreview
          testContacts={campaignTesting.testContacts}
          noNodesInFlow={noNodesInFlow}
          notEveryNodeHasFunctionName={notEveryNodeHasFunctionName}
          loading={campaignTesting.loading}
          activeTestContactIndex={campaignTesting.activeTestContactIndex}
          activeMessageIndex={campaignTesting.activeMessageIndex}
          handleNextContact={handleNextContact}
          handlePrevContact={handlePrevContact}
          handleNextMessage={handleNextMessage}
          handlePrevMessage={handlePrevMessage}
        />
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  section: {
    position: 'relative',
    padding: '0 48px 24px 48px',
  },
  arrow: {
    display: 'none',
    position: 'absolute',
    top: '-6px',
    left: '0px',
    fontSize: theme.typography.pxToRem(32),
    color: theme.palette.primary.main,
    '&.active': {
      display: 'inline-block',
    },
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  loadingText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
  },
}));

export default CampaignTesting;
